import { camelCase } from "lodash";

import { FileAnswer, OhCaseDocumentFile } from "@teamprevent/common/questionnaires/model";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { CustomQuestionConfig } from "../../questionnaires/questionnaireConfig";
import { CustomQuestionsQuestionnaireSectionProps } from "../../questionnaires/QuestionnaireSectionProps";
import { EmployeeRecord, AnsweredQuestionWithId, LinkObject } from "../../model";
import { TaskModel } from "../../task/taskActions/models";

export type HealthSurveillanceSectionProps = CustomQuestionsQuestionnaireSectionProps & { recordMedicalsSeparately: boolean };

export type MedicalCategory = "HealthSurveillance" | "FitForWork";

export interface MedicalType {
    id: MedicalTypeId;
    name: string;
    category: MedicalCategory;
    isObsolete?: boolean;
}

export interface CustomMedicalType {
    id: string;
    name: string;
    category: MedicalCategory;
    questionnaireSectionIds: string[];
    consultationSectionIds: string[];
}

export interface HealthSurveillanceQuestionConfig extends CustomQuestionConfig {
    medicalTypes: MedicalTypeId[];
}

export interface HealthSurveillanceSectionConfig {
    id: string;
    title: string;
    label?: string;
    medicalTypes: readonly MedicalTypeId[];
    questionConfigs: (CustomQuestionConfig & { medicalTypes?: MedicalTypeId[] })[];
}
export type HealthSurveillanceSectionsConfig<TKey extends string> = Record<TKey, HealthSurveillanceSectionConfig>;

/**
 * @deprecated props for OldHealthSurveillanceConsultation which itself is now deprecated due to not working with custom medicals.
 */
export type HealthSurveillanceProps = {
    readonly?: boolean;
    questionConfigs: CustomQuestionConfig[];
    saveQuestionnaire?: (questions: AnsweredQuestionWithId[], questionnaireKey?: string, eTag?: string) => Promise<void | string>;
    submitQuestionnaire?: (questions: AnsweredQuestionWithId[]) => Promise<boolean>;
    getOrgLevels?: () => Promise<LinkObject[]>;
    recordMedicalsSeparately?: boolean;
    removeMedicalSections?: boolean;
    autoFill?: boolean;
    employeeRecord?: EmployeeRecord;
}

/**
 * @deprecated HealthSurveillanceQuestionnaireProps is deprecated.
 * props for OldHealthSurveillanceConsultation which itself is now deprecated due to not working with custom medicals
 */
// eslint-disable-next-line deprecation/deprecation
export type HealthSurveillanceQuestionnaireProps = HealthSurveillanceProps & { task: TaskModel } & SupportingDocumentsProps;

export interface SupportingDocumentsProps {
    fileUploadApi: string;
    onFileUpload?: (file: File) => Promise<string | OhCaseDocumentFile>;
    onDeleteFile?: (fileId: string, fileName: string, ohCaseDocumentId: string) => Promise<void>;
    getDownloadLink?: (fileAnswer: FileAnswer) => string;
}

export const MedicalTypeIds = {
    Skin: "Skin",
    SkinFaceToFace: "SkinFaceToFace",
    Audiometry: "Audiometry",
    RespiratorySensitiser: "RespiratorySensitiser",
    RespiratorySensitiserFaceToFaceLevel1: "RespiratorySensitiserFaceToFaceLevel1",
    RespiratorySensitiserQuestionnaireLevel1: "RespiratorySensitiserQuestionnaireLevel1",
    HAVS: "HAVS",
    HAVSFaceToFace: "HAVSFaceToFace",
    IonisingRadiation: "IonisingRadiation",
    IonisingRadiationFaceToFace: "IonisingRadiationFaceToFace",
    BiologicalMonitoring: "BiologicalMonitoring",
    OtherSurveillance: "OtherSurveillance",
    SafetyCriticalWorker: "SafetyCriticalWorker",
    GroupOneDriving: "GroupOneDriving",
    GroupTwoDriving: "GroupTwoDriving",
    WorkAtHeight: "WorkAtHeight",
    WorkingConfinedSpaces: "WorkingConfinedSpaces",
    WearingRPE: "WearingRPE",
    WearingBreathingApparatus: "WearingBreathingApparatus",
    WorkingThermalEnvironment: "WorkingThermalEnvironment",
    NightWorker: "NightWorker",
    LoneWorker: "LoneWorker",
    FoodHandlerQuestionsOnly: "FoodHandlerQuestionsOnly",
    DSE: "DSE",
    Offshore: "Offshore",
    Airside: "Airside",
    OtherFitnessToWork: "OtherFitnessToWork",
    ManualHandlingMSKQuestionnaire: "ManualHandlingMSKQuestionnaire",
    ManualHandlingMSKFaceToFaceAppointment: "ManualHandlingMSKFaceToFaceAppointment",
    AmzPitPupMedical: "AmzPitPupMedical",
    AmzPitPupHealthDeclaration: "AmzPitPupHealthDeclaration",
    LAAWorker: "LAAWorker",
    FieldForce: "FieldForce",
    SkinLevel1: "SkinLevel1",
    SkinFaceToFaceLevel1: "SkinFaceToFaceLevel1",
    SkinAssessmentLevel2: "SkinAssessmentLevel2"
} as const;
// Can be one of the hardcoded values or a string (e.g. from the custom medicals part of database)
export type MedicalTypeId = typeof MedicalTypeIds[keyof typeof MedicalTypeIds] | string;

export const faceToFaceMedicals: MedicalTypeId[] = [
    MedicalTypeIds.SkinFaceToFace,
    MedicalTypeIds.IonisingRadiationFaceToFace,
    MedicalTypeIds.HAVSFaceToFace,
    MedicalTypeIds.ManualHandlingMSKFaceToFaceAppointment,
    MedicalTypeIds.SkinFaceToFaceLevel1,
    // MedicalTypeIds.AmzPitPupMedical and MedicalTypeIds.SkinAssessmentLevel2
    // consider these FaceToFace medical, but not quite (excluded from here in order to show the medical on Review form)
];

export { medicalTypes } from "./medicalTypes";

export enum MedicalOutcome {
    issue = "Issue Identified - NFA",
    noIssue = "No Issue Identified - NFA",
    decisionPending = "Decision Pending",
    fitWithAdjustments = "Fit with Adjustments - NFA",
    fit = "Fit - NFA",
    unfit = "Unfit - NFA",
    ohaToReview = "OHA to Review"
}

const enableHealthSurveillanceCrs11_3 = getFeatureToggles()[FeatureToggleNames.EnableHealthSurveillanceCrs11_3];

export const MedicalOutcomes: {[medicalCategory in MedicalCategory]: MedicalOutcome[]} = !enableHealthSurveillanceCrs11_3 ? {
    HealthSurveillance: [
        MedicalOutcome.issue,
        MedicalOutcome.noIssue,
        MedicalOutcome.decisionPending,
        MedicalOutcome.ohaToReview
    ],
    FitForWork: [
        MedicalOutcome.fitWithAdjustments,
        MedicalOutcome.fit,
        MedicalOutcome.decisionPending,
        MedicalOutcome.unfit,
        MedicalOutcome.ohaToReview
    ]
} : {
    HealthSurveillance: [
        MedicalOutcome.issue,
        MedicalOutcome.noIssue,
        MedicalOutcome.decisionPending,
    ],
    FitForWork: [
        MedicalOutcome.fitWithAdjustments,
        MedicalOutcome.fit,
        MedicalOutcome.decisionPending,
        MedicalOutcome.unfit,
    ]
};

export interface MedicalOutcomeModel {
    medicalTypeId: MedicalTypeId;
    outcome: MedicalOutcome;
    clinicalNotes: string;
    decisionPendingClarification: DecisionPendingClarification;
    customerNotes: string;
    recallYears?: number;
    recallWeeks?: number;
}

export type HealthSurveillanceOutcomeModel = {
    medicalTypeId: MedicalTypeId;
    outcome: keyof typeof MedicalOutcome;
    clinicalNotes: string;
    customerNotes: string;
    decisionPendingClarification: string;
    recallYears?: number;
    recallWeeks?: number;
}

export type ReviewHealthSurveillanceResultsModel = {
    outcomes: HealthSurveillanceOutcomeModel[];
}

export const parseReviewHealthSurveillanceResultsModel = (model: ReviewHealthSurveillanceResultsModel): ReviewHealthSurveillanceResultsModel => ({
    ...model,
    outcomes: model.outcomes.map(o => ({ ...o, outcome: camelCase(o.outcome) as keyof typeof MedicalOutcome }))
});

export enum HAVS_TIERS {
    TierOne = "Tier One",
    TierTwo = "Tier Two",
    TierThree = "Tier Three",
    TierFour = "Tier Four"
}

export const HAVS_TIER_OPTIONS = [
    HAVS_TIERS.TierOne,
    HAVS_TIERS.TierTwo,
    HAVS_TIERS.TierThree,
    HAVS_TIERS.TierFour,
].map(o => ({ text: o, value: o }));

export const HAVS_TIER_ONE_OUTCOME_OPTIONS = [
    "Fit to work with vibrating hand tools.  Tier 2 assessment questionnaire recommended in 6 months",
    "Fit to work with vibrating hand tools.  Tier 2 assessment questionnaire recommended in 12 months",
    "Tier 3 assessment recommended"
].map(o => ({ text: o, value: o }));

export const HAVS_TIER_TWO_OUTCOME_OPTIONS = [
    "Fit to work with vibrating hand tools.  Tier 2 assessment questionnaire recommended in 6 months",
    "Fit to work with vibrating hand tools.  Tier 2 assessment questionnaire recommended in 12 months",
    "Fit to work with vibrating hand tools.  Tier 2 re-assessment questionnaire and 3 yearly consultation"
    + " with Occupational Health Professional to discuss signs and symptoms of HAVS recommended in 12 months",
    "Tier 3 assessment recommended"
].map(o => ({ text: o, value: o }));

export const PRESENT_ABSENT_OPTIONS = [
    "Present",
    "Absent"
];

export const POSITIVE_NEGATIVE_OPTIONS = [
    "Positive",
    "Negative"
];

export const NORMAL_ABNORMAL_OPTIONS = [
    "Normal",
    "Abnormal"
];

export const RIGHT_LEFT_HAND_OPTIONS = [
    "Right handed",
    "Left handed"
];

export const RIGHT_LEFT_OPTIONS = [
    "Right",
    "Left"
];

export enum DecisionPendingClarification {
    Fit = "Fit",
    NotFit = "NotFit"
};

export const DecisionPendingClarificationOptions = [{
    value: DecisionPendingClarification.Fit,
    text: "The individual is fit to continue undertaking this aspect of their role until this is further reviewed and a decision determined."
}, {
    value: DecisionPendingClarification.NotFit,
    text: "The individual is not fit to continue undertaking this aspect of their role until this is further reviewed and a decision determined."
}];
